import { useState } from 'react'
import { Box, Divider, styled, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Icon } from 'packages/eid-icons'
import InboxStatusIndicator from 'components/InboxStatusIndicator'
import DateTimeGenericComponent from 'components/DynamicComponents/DateTimeGenericComponent'
import { SubjectTypeAttribute } from 'components'

// ─── Styled Components ──────────────────────────────────────────────────────

const ExpandedRowContainer = styled(Box)({
    width: '100%',
    paddingBottom: '16px',
    backgroundColor: '#EEEEF1',
})

const DetailedInfoBanner = styled(Box)({
    borderBottom: '1px solid #DEDEE3 !important',
    padding: '10px 24px',
    width: '100%',
    textTransform: 'uppercase',
    backgroundColor: '#FFF',
})

const BannerTitle = styled(Typography)({
    fontSize: '18px',
    fontWeight: 700,
    color: '#3b3b3b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
})

const DetailCard = styled(Box)({
    background: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    margin: '16px 20px',
    padding: '28px 32px',
})

const DetailGrid = styled(Box)({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
})

const DetailItem = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
})

const DetailLabel = styled(Typography)({
    fontSize: '12px',
    fontWeight: 700,
    color: '#8b909a',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
})

const DetailValue = styled(Typography)({
    fontSize: '14px',
    fontWeight: 400,
    color: '#282828',
    lineHeight: 1.5,
})

const ValidationCard = styled(Box)({
    background: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    margin: '0 20px 16px 20px',
    overflow: 'hidden',
})

const ValidationHeader = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 28px',
})

const ValidationTitle = styled(Typography)({
    fontSize: '18px',
    fontWeight: 800,
    color: '#282828',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
})

const ValidationSection = styled(Box)({
    padding: '20px 28px',
})

const Badge = styled(Box)(({ borderColor, bgColor, textColor }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '5px 14px',
    borderRadius: '4px',
    border: `1px solid ${borderColor}`,
    backgroundColor: bgColor,
    color: textColor,
    fontSize: '13px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    flexShrink: 0,
}))

const CheckRow = styled(Box)({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '5px 0',
})

const ShowAllChecksButton = styled(Box)({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    color: '#0057a0',
    fontSize: '13px',
    fontWeight: 700,
    textTransform: 'uppercase',
    cursor: 'pointer',
    marginTop: '16px',
    letterSpacing: '0.3px',
    '&:hover': {
        textDecoration: 'underline',
    },
})

const StyledDivider = styled(Divider)({
    borderColor: '#ebebeb',
})

const PublishCard = styled(Box)({
    background: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    margin: '0 20px 16px 20px',
    overflow: 'hidden',
})

const PublishHeader = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 28px',
})

const PublishTitle = styled(Typography)({
    fontSize: '18px',
    fontWeight: 800,
    color: '#282828',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
})

const PublishSection = styled(Box)({
    padding: '20px 28px',
})

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * Common ExpandedRow component for CSV Inbox items
 * @param {Object} props
 * @param {Object} props.data - The item data object
 * @param {Array<{key: string, labelKey: string}>} props.validationCheckFields - Array of validation field configurations
 * @param {boolean} [props.shouldCheckProcessStatus=false] - Whether to check processStatus !== 0 before showing validation results
 */
export default function ExpandedRowDetails({
    data,
    validationCheckFields = [],
    shouldCheckProcessStatus = false,
    processErrorField = null,
}) {
    const { t } = useTranslation()
    const [showAllChecks, setShowAllChecks] = useState(false)

    if (!data) return null

    // ── Parse validation checks ──────────────────────────────────────────
    const failedChecks = []
    const passedChecks = []

    validationCheckFields.forEach(({ key, labelKey }) => {
        const value = data[key]
        const label = t(labelKey)
        if (value !== null && value !== undefined && value !== '') {
            failedChecks.push({ label, message: value })
        } else {
            passedChecks.push({ label })
        }
    })

    // Status-based error routing ─────────────────────────────────────────
    // 'Error' means a process/publish pipeline failure; anything else
    // (e.g. 'Invalid') is a validation failure.
    const isProcessingError = data.rtA_CSVInboxItemStatusName === 'Error'

    const processingErrorMessage =
        isProcessingError && processErrorField
            ? data[processErrorField] || ''
            : ''

    // When the status is NOT a process error, the same processErrorField
    // (e.g. errorMessage for GroupAttributes) doubles as the validation
    // failure message when no validation_LastFailedError is present.
    const validationErrorMessage =
        data.validation_LastFailedError ||
        (!isProcessingError && processErrorField
            ? data[processErrorField] || ''
            : '') ||
        ''

    const hasValidationError =
        !!validationErrorMessage || failedChecks.length > 0

    const activeErrorMessage = isProcessingError
        ? processingErrorMessage
        : validationErrorMessage
    const activeErrorLabel = isProcessingError
        ? 'ProcessingError'
        : 'ValidationError'

    // Only show results card when there is something to display.
    // Processing errors always show when there is a message.
    const hasValidationResults = isProcessingError
        ? !!processingErrorMessage
        : shouldCheckProcessStatus
          ? data.processStatus !== 0 &&
            (hasValidationError ||
                passedChecks.length > 0 ||
                data.validation_Status === 'Valid')
          : hasValidationError ||
            passedChecks.length > 0 ||
            data.validation_Status === 'Valid'

    return (
        <ExpandedRowContainer>
            {/* ── DETAILED INFORMATION banner ────────────────────── */}
            <DetailedInfoBanner>
                <BannerTitle>{t('DetailedInformation')}</BannerTitle>
            </DetailedInfoBanner>

            {/* ── Detail attributes card ─────────────────────────── */}
            <DetailCard>
                <DetailGrid>
                    {/* Item Status */}
                    <DetailItem>
                        <DetailLabel>{t('ItemStatus')}</DetailLabel>
                        <InboxStatusIndicator
                            variant={
                                data.rtA_CSVInboxItemStatusName || 'Default'
                            }
                            label={t(
                                data.rtA_CSVInboxItemStatusName || 'Unknown',
                            )}
                            size="large"
                            minWidth="120px"
                        />
                    </DetailItem>

                    {/* Processing Status */}
                    <DetailItem>
                        <DetailLabel>{t('ProcessingStatus')}</DetailLabel>
                        <DetailValue>
                            {data.processStatusName || '-'}
                        </DetailValue>
                    </DetailItem>

                    {/* Last Updated */}
                    <DetailItem>
                        <DetailLabel>{t('LastUpdated')}</DetailLabel>
                        <Box display="flex" alignItems="center" gap="8px">
                            <Icon
                                name="Clock"
                                style={{
                                    width: '16px',
                                    height: '16px',
                                    opacity: 0.5,
                                }}
                            />
                            <DetailValue>
                                <DateTimeGenericComponent
                                    data={data}
                                    attribute={{
                                        name: 'modifiedDate',
                                    }}
                                />
                            </DetailValue>
                        </Box>
                    </DetailItem>

                    {/* Updated By / Published By */}
                    <DetailItem>
                        <DetailLabel>
                            {data.modifiedBy
                                ? t('UpdatedBy')
                                : data.publishedBy
                                ? t('PublishedBy')
                                : t('UpdatedBy')}
                        </DetailLabel>
                        {data.modifiedBy || data.publishedBy ? (
                            <SubjectTypeAttribute
                                subjectType="Person"
                                item={data}
                                personComponentProps={{
                                    nameProp: data.modifiedBy
                                        ? 'modifiedByFriendlyName'
                                        : 'publishedByFriendlyName',
                                    imgSrcProp: 'imageUrl',
                                }}
                            />
                        ) : (
                            <DetailValue>-</DetailValue>
                        )}
                    </DetailItem>

                    {/* Comments */}
                    <DetailItem>
                        <DetailLabel>{t('Comments')}</DetailLabel>
                        <DetailValue>{data.comments || '-'}</DetailValue>
                    </DetailItem>
                </DetailGrid>
            </DetailCard>

            {/* ── VALIDATION RESULTS ─────────────────────────────── */}
            {hasValidationResults && (
                <ValidationCard>
                    {/* Header */}
                    <ValidationHeader>
                        <ValidationTitle>
                            {t('Results')}
                        </ValidationTitle>
                        {data.validation_LastFailed && (
                            <Box display="flex" alignItems="center" gap="8px">
                                <DetailLabel
                                    sx={{
                                        margin: 0,
                                        fontSize: '11px',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    {t('LastFailedOn')}
                                </DetailLabel>
                                <Icon
                                    name="Clock"
                                    style={{
                                        width: '15px',
                                        height: '15px',
                                        opacity: 0.5,
                                    }}
                                />
                                <DateTimeGenericComponent
                                    data={data}
                                    attribute={{
                                        name: 'validation_LastFailed',
                                    }}
                                />
                            </Box>
                        )}
                        {!data.validation_LastFailed &&
                            data.validation_SucceedDate && (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap="8px"
                                >
                                    <DetailLabel
                                        sx={{
                                            margin: 0,
                                            fontSize: '11px',
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        {t('LastSucceededOn')}
                                    </DetailLabel>
                                    <Icon
                                        name="Clock"
                                        style={{
                                            width: '15px',
                                            height: '15px',
                                            opacity: 0.5,
                                        }}
                                    />
                                    <DateTimeGenericComponent
                                        data={data}
                                        attribute={{
                                            name: 'validation_SucceedDate',
                                        }}
                                    />
                                </Box>
                            )}
                    </ValidationHeader>

                    <StyledDivider />

                    {/* Validation / Processing Error */}
                    {activeErrorMessage && (
                        <>
                            <ValidationSection>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap="20px"
                                >
                                    <Badge
                                        borderColor="#d0021b"
                                        bgColor="#fff5f5"
                                        textColor="#d0021b"
                                    >
                                        {t(activeErrorLabel)}
                                    </Badge>
                                    <DetailValue>
                                        {activeErrorMessage}
                                    </DetailValue>
                                </Box>
                            </ValidationSection>

                            {!isProcessingError && failedChecks.length > 0 && <StyledDivider />}
                        </>
                    )}

                    {/* Failed Checks */}
                    {!isProcessingError && failedChecks.length > 0 && (
                        <ValidationSection>
                            <Box
                                display="flex"
                                alignItems="flex-start"
                                gap="20px"
                            >
                                <Badge
                                    borderColor="#d0021b"
                                    bgColor="#fff5f5"
                                    textColor="#d0021b"
                                    sx={{ marginTop: '4px' }}
                                >
                                    {t('FailedChecks')}
                                </Badge>
                                <Box>
                                    {failedChecks.map((check, idx) => (
                                        <CheckRow key={idx}>
                                            <Icon
                                                name="Close"
                                                color="#d0021b"
                                                style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    marginTop: '3px',
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontSize: '14px',
                                                    color: '#282828',
                                                    lineHeight: 1.6,
                                                }}
                                            >
                                                <strong>{check.label}:</strong>{' '}
                                                {check.message}
                                            </Typography>
                                        </CheckRow>
                                    ))}
                                </Box>
                            </Box>
                        </ValidationSection>
                    )}

                    {/* Passed Checks - Always show label if there are passed checks */}
                    {passedChecks.length > 0 && (
                        <>
                            {(hasValidationError ||
                                failedChecks.length > 0) && <StyledDivider />}
                            <ValidationSection>
                                {/* Header with badge on left and button on right */}
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    marginBottom={
                                        !hasValidationError || showAllChecks
                                            ? '20px'
                                            : 0
                                    }
                                >
                                    <Badge
                                        borderColor="#1eb77b"
                                        bgColor="#f0faf5"
                                        textColor="#1eb77b"
                                        sx={{ marginTop: '4px' }}
                                    >
                                        {t('PassedChecks')}
                                    </Badge>
                                    {/* Show/Hide button - only if there's a validation error */}
                                    {hasValidationError && (
                                        <ShowAllChecksButton
                                            onClick={() =>
                                                setShowAllChecks(
                                                    (prev) => !prev,
                                                )
                                            }
                                            sx={{
                                                marginTop: 0,
                                                marginBottom: 0,
                                            }}
                                        >
                                            {showAllChecks
                                                ? t('HidePassedChecks')
                                                : t('ShowAllChecks')}
                                            <Icon
                                                name={
                                                    showAllChecks
                                                        ? 'MinusCircle'
                                                        : 'PlusCircle'
                                                }
                                                color="#0057a0"
                                                style={{
                                                    width: '18px',
                                                    height: '18px',
                                                }}
                                            />
                                        </ShowAllChecksButton>
                                    )}
                                </Box>
                                {/* Show passed checks details if no validation error OR if showAllChecks is true */}
                                {(!hasValidationError || showAllChecks) && (
                                    <Box>
                                        {passedChecks.map((check, idx) => (
                                            <CheckRow key={idx}>
                                                <Icon
                                                    name="Check"
                                                    color="#1eb77b"
                                                    style={{
                                                        width: '16px',
                                                        height: '16px',
                                                        marginTop: '3px',
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        fontSize: '14px',
                                                        color: '#282828',
                                                        lineHeight: 1.6,
                                                    }}
                                                >
                                                    <strong>
                                                        {check.label}:
                                                    </strong>{' '}
                                                    {t('Passed')}
                                                </Typography>
                                            </CheckRow>
                                        ))}
                                    </Box>
                                )}
                            </ValidationSection>
                        </>
                    )}
                </ValidationCard>
            )}

            {/* ── PUBLISH SECTION (for completed items) ───────────────────── */}
            {data.rtA_CSVInboxItemStatusName === 'Completed' &&
                data.publishedDate && (
                    <PublishCard>
                        {/* Header */}
                        <PublishHeader>
                            <PublishTitle>{t('Publish')}</PublishTitle>
                            <Box display="flex" alignItems="center" gap="16px">
                                {/* Published By */}
                                {data.publishedByFriendlyName && (
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap="8px"
                                    >
                                        <DetailLabel
                                            sx={{
                                                margin: 0,
                                                fontSize: '11px',
                                                textTransform: 'uppercase',
                                            }}
                                        >
                                            {t('PublishedBy')}
                                        </DetailLabel>
                                        <DetailValue
                                            sx={{
                                                fontSize: '14px',
                                                color: '#282828',
                                            }}
                                        >
                                            {data.publishedByFriendlyName}
                                        </DetailValue>
                                    </Box>
                                )}
                                {/* Publish Date */}
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap="8px"
                                >
                                    <DetailLabel
                                        sx={{
                                            margin: 0,
                                            fontSize: '11px',
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        {t('PublishDate')}
                                    </DetailLabel>
                                    <Icon
                                        name="Clock"
                                        style={{
                                            width: '15px',
                                            height: '15px',
                                            opacity: 0.5,
                                        }}
                                    />
                                    <DateTimeGenericComponent
                                        data={data}
                                        attribute={{
                                            name: 'publishedDate',
                                        }}
                                    />
                                </Box>
                            </Box>
                        </PublishHeader>

                        <StyledDivider />

                        {/* Published Assignment */}
                        <PublishSection>
                            <Box
                                display="flex"
                                alignItems="flex-start"
                                gap="20px"
                            >
                                <Badge
                                    borderColor="#0057a0"
                                    bgColor="#e7f4ff"
                                    textColor="#0057a0"
                                >
                                    {t('PublishedAssignment')}
                                </Badge>
                                <DetailValue
                                    sx={{
                                        fontSize: '14px',
                                        lineHeight: 1.6,
                                        color: '#282828',
                                        whiteSpace: 'pre-line',
                                        alignSelf:'center',
                                    }}
                                >
                                    {data.publishedAssignmentFriendlyName ||
                                        '-'}
                                </DetailValue>
                            </Box>
                        </PublishSection>
                    </PublishCard>
                )}
        </ExpandedRowContainer>
    )
}
